---
title: "A resume didn't parse"
description: "A resume came back blank, half-filled, or wrong in Chosen. The usual causes — scanned PDFs, very long resumes, bad files — and how to fix each one."
category: "troubleshooting"
keywords:
  - "resume parsing failed"
  - "resume not parsing"
  - "blank candidate fields"
  - "resume parser empty"
  - "fix resume parsing"
schema: "HowTo"
nav:
  section: "troubleshooting"
  order: 10
  parent: "/troubleshooting"
---

You uploaded a resume and the candidate's fields are empty, half-filled, or just wrong. The candidate record still exists — nothing was lost — and in most cases the fix is one of three things below. Start with the most likely cause and work down.

If you only want the short version: try **Reparse** first, and if that doesn't help, the file itself is probably the problem.

## The file is a scanned or image-only PDF

This is the most common cause of a fully blank parse. [Resume parsing](/guides/candidates/resume-parsing) reads the *text* in a file. A PDF that's really a photo of a page — scanned from paper, or exported as an image — has no text to read, so the parser finds almost nothing and the fields come back empty.

**Check it:** open the file and try to select text with your cursor. If you can highlight words, there's text. If the whole page selects as one block or nothing selects at all, it's an image.

**Fix it:**

<Steps>
  <li>Ask the candidate for the original document — a real PDF exported from a word processor, or a DOCX.</li>
  <li>Upload the new file to the candidate record.</li>
  <li>If you can't get a better file, open the candidate's detail panel and fill the fields in by hand. Your edits become what everyone sees and what search uses.</li>
</Steps>

## The resume is very long and lost its tail

Parsing reads roughly the first 35,000 characters of a resume. A normal one- or two-page resume is nowhere near that limit. A fifteen-page academic CV or a decades-long work history can pass it — and anything past the cutoff, usually the oldest roles, simply isn't extracted.

**Check it:** the top of the profile (name, contact, recent roles) looks right, but older history or a publications list is missing.

**Fix it:** there's no setting to raise the cap. Open the candidate's detail panel and add the missing roles by hand. If the older history doesn't matter for this hire, you can leave it — the recent roles drive most of the [match rating](/guides/candidates/match-rating) anyway.

## The file is unsupported or corrupt

Chosen parses **PDF and DOCX** resumes. Anything else — a `.pages` file, an `.rtf`, an image, a link to a Google Doc — has nothing the parser can open. A genuinely corrupt PDF fails the same way.

**Check it:** confirm the file extension is `.pdf` or `.docx`, and that the file opens normally on your own machine.

**Fix it:** re-export the resume as a standard PDF or DOCX and upload that. If it opens fine for you but still won't parse, replace it with a fresh export — a re-save often clears a subtle corruption.

## Parsing is still running, or it failed

Parsing happens in the background, not while you wait. A resume moves through states: **Queued**, **Running**, **Succeeded**, **Failed**. Empty fields right after an upload may just mean the parse hasn't finished — for a bulk import, candidates appear immediately and fill in one at a time.

**Check it:** give a single upload a moment. If the candidate sits in **Failed**, the parse genuinely didn't complete.

**Fix it:**

<Steps>
  <li>Open the candidate's detail panel.</li>
  <li>Use **Reparse** to re-run the parser against the original file.</li>
  <li>If it fails again, the file is the problem — replace it using the steps above, then reparse once more.</li>
</Steps>

<Callout type="tip" title="Reparse after a hand-edit if search still can't find them">
Editing a field by hand fixes what you see. But if a candidate isn't turning up in search you'd expect them to, run **Reparse** — a reparse refreshes everything Chosen searches on, not just the visible field. This also catches a candidate up after you add a new auto-fill [custom field](/guides/candidates/custom-fields).
</Callout>

## Still stuck?

If a resume that clearly has selectable text still parses to nothing after a reparse, that's worth reporting. Open **Settings → Contact support**, describe the file, and attach it if you can.
